import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ChevronLeft } from 'lucide-react';
import { profileAPI } from '../../utils/api';
import useUserStore from '../../store/userStore';
import { toast } from 'sonner';
import useKeyboardAware from '../../hooks/useKeyboardAware';
import { MANUAL_MACROS_BACK_ROUTE } from './onboardingFlowLogic';
import { checkManualMacros } from '../../utils/macroConsistency';
import MacroMismatchNotice from '../../components/MacroMismatchNotice';

const ManualMacros = () => {
  const navigate = useNavigate();
  const { setProfile, profile, user } = useUserStore();
  // Auto-scroll focused input into view when iOS keyboard opens.
  // Without this, the keyboard covers the input fields on number entry.
  // 380ms (vs default 280) gives the iOS predictive-text bar time to
  // settle before scroll math runs, so lower fields (carbs, fat) center
  // correctly on iOS 17+.
  useKeyboardAware({ delayMs: 380 });
  
  // Derive demo mode from user presence
  const isDemoMode = user?.user_id?.startsWith('demo_');
  const [macros, setMacros] = useState({
    calories: '',
    protein: '',
    carbs: '',
    fat: '',
  });
  const [loading, setLoading] = useState(false);
  // 4/4/9: the calorie total must equal protein×4 + carbs×4 + fat×9. Manual
  // mode keeps the grams exactly as entered; a mismatch is shown BEFORE
  // saving and resolved only by the user's explicit "Use N calories" tap.
  const check = checkManualMacros(macros);

  const applyDerivedCalories = () => {
    if (check.expectedCalories === null) return;
    setMacros({ ...macros, calories: String(check.expectedCalories) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Never save an inconsistent four-value target set (the server rejects it too).
    if (!check.consistent) return;
    setLoading(true);

    try {
      const macroData = {
        calories: parseInt(macros.calories),
        protein: parseInt(macros.protein),
        carbs: parseInt(macros.carbs),
        fat: parseInt(macros.fat),
      };
      
      if (!isDemoMode) {
        await profileAPI.updateMacros(macroData);
        // Mirror exactly what PUT /profile/macros now persists (both naming
        // conventions + auto_macros_enabled:false), preserving the rest of
        // the existing profile/goal state — otherwise the in-memory profile
        // would still show stale/no targets and auto_macros_enabled left at
        // its previous value until the next full profile refetch (see
        // AGENTS.md pre-launch audit nutrition-readiness follow-up).
        setProfile({
          ...profile,
          ...macroData,
          target_calories: macroData.calories,
          target_protein_g: macroData.protein,
          target_carbs_g: macroData.carbs,
          target_fat_g: macroData.fat,
          auto_macros_enabled: false,
        });
      } else {
        // In demo mode, store macros locally
        setProfile({ ...profile, ...macroData, auto_macros_enabled: false });
      }

      toast.success('Macros saved successfully');
      navigate('/onboarding/allergens', { state: { mode: 'manual' } });
    } catch (error) {
      // 402 = subscription_required. The global axios interceptor in api.js
      // already opens the paywall — showing "Failed to save macros" on top
      // of it would be a misleading double signal. Bail silently here.
      // (For onboarding, the backend should not return 402 anymore — but
      //  defending the UX in case of post-onboarding edits.)
      if (error?.response?.status === 402) {
        return;
      }
      // A server-side 4/4/9 rejection: surface the derived total; values unchanged.
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 422 && detail?.error === 'macros_inconsistent') {
        toast.error(`Calories should be ${detail.expected_calories} for these macros.`);
        return;
      }
      toast.error('Failed to save macros');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen header-safe-top px-6 pb-48">
      <motion.div
        initial={{ opacity: 0, y: 0 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md mx-auto space-y-8"
      >
        <button
          onClick={() => navigate(MANUAL_MACROS_BACK_ROUTE.path, { state: MANUAL_MACROS_BACK_ROUTE.state })}
          data-testid="manual-macros-back-btn"
          className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft size={18} /> Back
        </button>

        <div className="text-center space-y-2">
          <h1 className="font-heading font-bold text-3xl" data-testid="manual-macros-title">Enter Your Macros</h1>
          <p className="text-muted-foreground">Input your daily macro targets</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="calories">Calories</Label>
              <Input
                id="calories"
                type="number"
                data-testid="calories-input"
                placeholder="2000"
                value={macros.calories}
                onChange={(e) => setMacros({ ...macros, calories: e.target.value })}
                required
                className="h-12 bg-secondary/30 border-transparent focus:border-accent"
              />
            </div>

            <div>
              <Label htmlFor="protein">Protein (g)</Label>
              <Input
                id="protein"
                type="number"
                data-testid="protein-input"
                placeholder="150"
                value={macros.protein}
                onChange={(e) => setMacros({ ...macros, protein: e.target.value })}
                required
                className="h-12 bg-secondary/30 border-transparent focus:border-accent"
              />
            </div>

            <div>
              <Label htmlFor="carbs">Carbs (g)</Label>
              <Input
                id="carbs"
                type="number"
                data-testid="carbs-input"
                placeholder="200"
                value={macros.carbs}
                onChange={(e) => setMacros({ ...macros, carbs: e.target.value })}
                required
                className="h-12 bg-secondary/30 border-transparent focus:border-accent"
              />
            </div>

            <div>
              <Label htmlFor="fat">Fat (g)</Label>
              <Input
                id="fat"
                type="number"
                data-testid="fat-input"
                placeholder="70"
                value={macros.fat}
                onChange={(e) => setMacros({ ...macros, fat: e.target.value })}
                required
                className="h-12 bg-secondary/30 border-transparent focus:border-accent"
              />
            </div>
          </div>

          <MacroMismatchNotice check={check} onUseDerived={applyDerivedCalories} />

          <Button
            type="submit"
            data-testid="save-macros-btn"
            disabled={loading || !check.consistent}
            className="w-full h-12 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-transform active:scale-95"
          >
            {loading ? 'Saving...' : 'Continue'}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

export default ManualMacros;
